import React, { useState } from "react";
import ScheduleModal from "./ScheduleModal";
import { shortPref, prefClass } from "../shiftPrefs";
import "./WorkerList.css";

function initialsFor(first, last) { return `${(first||"?")[0]}${(last||"?")[0]}`.toUpperCase(); }

// Caregivers are external (not Teams users), so a Teams user-call by email
// fails, and the Teams PSTN deep link needs a Teams Phone license. A plain
// tel: link is the reliable click-to-call: it hands the E.164 number to the
// machine's default calling app (Teams, if set as the tel: handler).
function e164(phone) {
  if (!phone) return null;
  const n = String(phone).trim();
  if (n.startsWith("+")) return "+" + n.slice(1).replace(/\D/g, "");
  const d = n.replace(/\D/g, "");
  if (d.length === 10) return "+1" + d;
  if (d.length === 11 && d[0] === "1") return "+" + d;
  return d ? "+" + d : null;
}
function callUrl(phone) {
  const n = e164(phone);
  return n ? `tel:${n}` : null;
}
function mapsUrl(workerAddress, clientAddress) {
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(workerAddress)}&destination=${encodeURIComponent(clientAddress)}&travelmode=driving`;
}
function fmtTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

const AVATAR_COLORS = [
  ["#1a3a2a","#3ecf8e"],["#1a2a3a","#4d9ef7"],["#2a1a3a","#a78bfa"],
  ["#3a2a1a","#f5a623"],["#2a1a1a","#e05c5c"],["#1a3a3a","#26C6DA"],
  ["#3a1a2a","#F48FB1"],["#2a3a1a","#AED581"],
];
function avatarColor(id) { return AVATAR_COLORS[(id?.charCodeAt(0)||0) % AVATAR_COLORS.length]; }

function WorkerCard({ worker, rank, clientAddress, weeklySchedule, weekStart, weekEnd }) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [bg, fg] = avatarColor(worker.id);
  const hasDist   = worker.miles !== undefined && worker.miles !== null;
  const schedule  = weeklySchedule?.[worker.id];
  const totalHours = schedule?.totalHours || 0;
  const isOvertime = totalHours >= 40;
  const hasShifts  = schedule?.shifts?.length > 0;

  return (
    <>
      <div className={`worker-card fade-up ${worker.isPreferred ? "preferred" : ""} ${worker.isDNR ? "dnr" : ""} ${worker.conflict ? "booked" : ""}`}
        style={{ animationDelay: `${rank * 40}ms` }}>
        <div className="card-rank">{worker.isDNR ? "✕" : `#${rank + 1}`}</div>
        <div className="card-avatar" style={{ background: bg, color: fg }}>
          {initialsFor(worker.firstName, worker.lastName)}
          {worker.isPreferred && <span className="preferred-dot" />}
        </div>
        <div className="card-info">
          <div className="card-name">
            {worker.firstName} {worker.lastName}
            {worker.isPreferred && <span className="preferred-tag">★ Preferred</span>}
            {worker.isDNR && <span className="dnr-tag">DNR</span>}
            {isOvertime && (
              <span className="overtime-badge" title={`${totalHours.toFixed(1)} hrs this week — at or over 40`}>
                ⚠ {totalHours.toFixed(1)}h
              </span>
            )}
            {!isOvertime && hasShifts && (
              <span className="hours-badge">{totalHours.toFixed(1)}h this week</span>
            )}
          </div>
          <div className="card-role-phone">
            <span className={`role-chip role-${worker.role?.toLowerCase()}`}>{worker.role}</span>
            {worker.phone && <span className="card-phone">{worker.phone}</span>}
          </div>
          {worker.shiftPref?.length > 0 && (
            <div className="pref-chips card-prefs" title="Shift preference (in the caregiver's ranked order)">
              {worker.shiftPref.map(p => <span key={p} className={`pref-chip ${prefClass(p)}`}>{shortPref(p)}</span>)}
            </div>
          )}
          {hasDist && !worker.isDNR && (
            <div className="card-distance">
              <span className="dist-miles">{worker.miles} mi</span>
              <span className="dist-sep">·</span>
              <span className="dist-time">~{worker.minutes} min drive</span>
            </div>
          )}
          {hasDist && !worker.isDNR && worker.minutes && (
            <div className="card-transit">
              <span className="transit-icon">🚌</span>
              <span className="transit-time">~{Math.round((worker.minutes * 2.5) / 5) * 5} min est. transit</span>
            </div>
          )}
          {!hasDist && !worker.isDNR && (
            <div className="card-distance loading-dist">
              <span className="spinner-sm" /> Calculating…
            </div>
          )}
          {worker.conflict && (
            <div className="conflict-alert">
              ⚠ Already booked {fmtTime(worker.conflict.start)} – {fmtTime(worker.conflict.end)} with {worker.conflict.clientName}
            </div>
          )}
          {worker.isDNR && <div className="dnr-reason">Excluded from this client (DNR)</div>}
        </div>
        {!worker.isDNR && (
          <div className="card-actions">
            {callUrl(worker.phone) && (
              <a className="action-btn primary"
                href={callUrl(worker.phone)}
                title={`Call ${worker.phone}`}>
                📞 Call
              </a>
            )}
            <button
              className="action-btn secondary schedule-btn"
              onClick={() => setShowSchedule(true)}
              title="View weekly schedule">
              📅 Schedule
            </button>
            {worker.address && clientAddress && (
              <a className="action-btn secondary"
                href={mapsUrl(worker.address, clientAddress)}
                target="_blank" rel="noopener noreferrer"
                title="Get driving directions">
                ↗ Directions
              </a>
            )}
          </div>
        )}
      </div>

      {showSchedule && (
        <ScheduleModal
          worker={worker}
          schedule={schedule}
          weekStart={weekStart}
          weekEnd={weekEnd}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </>
  );
}

export default function WorkerList({ workers, dnrWorkers = [], loading, hasSelection, activeClient, weeklySchedule, weekStart, weekEnd }) {
  if (!hasSelection) return (
    <div className="worker-list-empty">
      <div className="empty-icon">◈</div>
      <p>Select an open shift or client from the sidebar<br />to see nearby available workers</p>
    </div>
  );
  if (loading && workers.length === 0) return (
    <div className="worker-list-empty">
      <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
      <p style={{ marginTop: 12 }}>Loading workers…</p>
    </div>
  );

  return (
    <div className="worker-list">
      {workers.length === 0 && !loading && (
        <div className="worker-list-empty" style={{ height: "auto", padding: "32px 0" }}>
          <p>No available workers found within 90 minutes of this location</p>
        </div>
      )}
      {workers.map((worker, i) => (
        <WorkerCard key={worker.id} worker={worker} rank={i}
          clientAddress={activeClient?.address}
          weeklySchedule={weeklySchedule}
          weekStart={weekStart}
          weekEnd={weekEnd} />
      ))}
      {dnrWorkers.length > 0 && (
        <>
          <div className="section-divider">DNR — Do Not Return — excluded from this client</div>
          {dnrWorkers.map((worker, i) => (
            <WorkerCard key={worker.id} worker={worker} rank={i}
              clientAddress={activeClient?.address}
              weeklySchedule={weeklySchedule}
              weekStart={weekStart}
              weekEnd={weekEnd} />
          ))}
        </>
      )}
    </div>
  );
}
